import {
  Alert,
  Anchor,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconMapPin,
  IconPhone,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { CaptureSheet } from "../../capture";
import {
  ErrorState,
  InlineError,
  LoadingSkeleton,
  MoneyField,
  SectionCard,
  StateBadge,
  StickyActionBar,
} from "../../components/ui";
import { deliveryState } from "../../components/ui/statusBadge";
import { api, type DeliveryTaskDetail as Task } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";
import { STATUS_LABELS, carryingLabel, mapLink, partlyCollected } from "./deliveryMeta";

/**
 * One delivery, and the three things that can happen to it: take it, hand it
 * over, or record why it could not be handed over.
 *
 * The handover uses the shared CaptureSheet with kind="DELIVERY", which is
 * where REQ-DLV-2 becomes visible: PROOF_REQUIREMENTS makes photo, GPS and
 * signature all required, so submit stays disabled until all three exist. The
 * API re-checks it independently - this only saves the agent a round trip.
 *
 * A failed attempt is NOT that sheet. There is nobody at the door to sign, so
 * holding it to the same set would mean the honest outcome cannot be recorded
 * at all - which is exactly what the API did until the rehearsal caught it.
 */
export function DeliveryTaskDetail() {
  const { id = "" } = useParams();
  const { user, hasRole } = useAuth();

  const [task, setTask] = useState<Task>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"idle" | "deliver" | "fail">("idle");
  const [failureReason, setFailureReason] = useState("");
  const [collected, setCollected] = useState<number | string>("");
  // Up here with the other hooks, NOT beside the derived values further down.
  // It was declared after the two early returns for the loading and error
  // states, so on the render where the task arrived React saw one more hook
  // than the render before it and threw #310. A browser drive caught it; a
  // typecheck never could.
  const [receivedBy, setReceivedBy] = useState("");

  const load = useCallback(async () => {
    try {
      setError(undefined);
      const { task: loaded } = await api.deliveryTask(id);
      setTask(loaded);
      // Pre-filled with what is actually outstanding, because that is what the
      // agent collects in the overwhelming majority of handovers. They can
      // change it; they should not have to type it.
      setCollected(loaded.collectsMoney ? loaded.outstandingAmount : "");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load this delivery.",
      );
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !task) {
    return (
      <Page title="Delivery" back="/delivery">
        <ErrorState message={error} onRetry={() => void load()} />
      </Page>
    );
  }

  if (!task) {
    return (
      <Page title="Delivery" back="/delivery">
        <LoadingSkeleton variant="detail" />
      </Page>
    );
  }

  const customer = task.order?.customer ?? task.serviceJob?.customer ?? null;
  const isManager = hasRole("STORE_MANAGER", "ADMIN");
  const isMine = task.assignedTo?.id === user?.id;
  /** Mirrors assertIsCarrier in delivery.service - the API is the authority. */
  const canClose = (isMine || isManager) && task.status !== "DELIVERED";
  const canTake =
    task.status !== "DELIVERED" && (!task.assignedTo || isMine || isManager);

  const collectedAmount = Number(collected);
  const collectedValid =
    String(collected).trim() === "" ||
    (Number.isFinite(collectedAmount) &&
      collectedAmount >= 0 &&
      collectedAmount <= Number(task.outstandingAmount) + 0.001);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await work();
      setMode("idle");
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  const maps = customer ? mapLink(customer) : undefined;

  /**
   * "Could not deliver" is the one irreversible-feeling step here, and the
   * brief's rule is that such a thing states its consequence. It does not
   * close the delivery - it records an attempt and the goods stay to be
   * delivered - so the modal says exactly that, because an agent hesitating
   * over this button is usually worried it cancels the job.
   */
  const thisTask = task;
  function confirmFailure() {
    modals.openConfirmModal({
      title: "Record a failed attempt",
      children: (
        <Text fz="md">
          This records why the handover did not happen. It does{" "}
          <strong>not</strong> cancel the delivery — the goods are still to be
          delivered, and this task stays open so somebody can try again.
        </Text>
      ),
      labels: { confirm: "Record the failed attempt", cancel: "Go back" },
      onConfirm: () =>
        void run(async () => {
          await api.failDelivery(thisTask.id, { reason: failureReason.trim() });
          setFailureReason("");
        }),
    });
  }

  return (
    <Page
      title={task.subjectReference}
      subtitle={carryingLabel(task)}
      back="/delivery"
    >
      {error && <InlineError>{error}</InlineError>}

      <SectionCard title="Where it is going">
        <Stack gap="sm">
          <Group gap="xs" wrap="wrap">
            <StateBadge
              state={deliveryState(task.status) ?? "dlvPending"}
              label={STATUS_LABELS[task.status] ?? task.status}
            />
            {task.assignedTo && (
              <StateBadge state="dlvAssigned" label={task.assignedTo.name} />
            )}
          </Group>

          {customer && (
            <Stack gap={4}>
              <Text fz="lg" fw={600} c="gray.9">
                {customer.name}
              </Text>
              {customer.phone && (
                <Group gap={6} wrap="nowrap">
                  <IconPhone size={15} stroke={1.8} color="var(--mantine-color-gray-6)" />
                  <Anchor href={`tel:${customer.phone}`} fz="md" className="num">
                    {customer.phone}
                  </Anchor>
                </Group>
              )}
              <Text fz="md" c="gray.7">
                {customer.address ?? "No address on record"}
              </Text>
              {/* Half the imported addresses are a line of free text. The
                  last capture's coordinates are often the only thing that
                  finds the place, so they are offered rather than buried. */}
              {maps && (
                <Group gap={6} wrap="nowrap">
                  <IconMapPin size={15} stroke={1.8} color="var(--mantine-color-gray-6)" />
                  <Anchor href={maps} target="_blank" rel="noreferrer" fz="md">
                    Open the last known location on a map
                  </Anchor>
                  {customer.geoNotes && (
                    <Text fz="sm" c="gray.6">
                      · {customer.geoNotes}
                    </Text>
                  )}
                </Group>
              )}
            </Stack>
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="What you are carrying">
        <Stack gap="sm">
          {task.order ? (
            <>
              <Stack gap={4}>
                {task.order.lineItems.map((line) => (
                  <Text key={line.id} fz="md" c="gray.9">
                    <span className="num">{line.product.sku}</span> —{" "}
                    {line.product.name} ×{" "}
                    <span className="num">{line.quantityFulfilled}</span>
                  </Text>
                ))}
              </Stack>
              {task.order.bill && (
                <Text fz="sm" c="gray.6">
                  Bill <span className="num">{task.order.bill.reference}</span> ·{" "}
                  <span className="num">{formatINR(task.order.bill.totalAmount)}</span>
                </Text>
              )}
            </>
          ) : (
            <Text fz="md" c="gray.9">
              {/* The model first, because it is what the agent can check
                  against the battery in their hand. The description is the
                  fallback for jobs collected before the model was recorded. */}
              <span className="num">
                {task.serviceJob?.collectedProduct?.sku ??
                  task.serviceJob?.batteryDetails ??
                  ""}
              </span>
              {task.serviceJob && task.serviceJob.itemCount > 1
                ? ` · ${task.serviceJob.itemCount} items`
                : ""}
            </Text>
          )}

          {task.collectsMoney ? (
            <Alert
              variant="light"
              color="yellow"
              radius="md"
              icon={<IconAlertTriangle size={18} stroke={1.8} />}
              title={`Collect ${formatINR(task.outstandingAmount)} on the doorstep`}
            >
              {partlyCollected(task)
                ? `The bill was ${formatINR(
                    task.order!.bill!.totalAmount,
                  )}; the rest has already been collected.`
                : "This is what is still outstanding now."}
            </Alert>
          ) : (
            <Alert
              variant="light"
              color="teal"
              radius="md"
              icon={<IconCircleCheck size={18} stroke={1.8} />}
            >
              Nothing to collect
              {task.carrying === "SERVICE_JOB"
                ? " — this is their own battery coming back."
                : task.order?.type === "FOC"
                  ? " — a free replacement."
                  : task.order?.type === "PRORATA"
                    ? " — the part charge is already settled."
                    : " — the bill is settled."}
            </Alert>
          )}
        </Stack>
      </SectionCard>

      {task.status === "FAILED" && task.failureReason && (
        <SectionCard title="The last attempt">
          <Stack gap="sm">
            <InlineError>{task.failureReason}</InlineError>
            <Text fz="md" c="gray.7">
              The goods are still to be delivered. Take it again to try once
              more.
            </Text>
          </Stack>
        </SectionCard>
      )}

      {task.status === "DELIVERED" && (
        <SectionCard title="Done">
          <Stack gap="sm">
            <Alert
              variant="light"
              color="teal"
              radius="md"
              icon={<IconCircleCheck size={18} stroke={1.8} />}
            >
              Handed over
              {task.deliveredAt
                ? ` on ${new Date(task.deliveredAt).toLocaleString()}`
                : ""}
              .
            </Alert>
            {task.order?.serviceJob && (
              <Text fz="md" c="gray.7">
                This also closed {task.order.serviceJob.reference} — the
                replacement it was raised for.
              </Text>
            )}
          </Stack>
        </SectionCard>
      )}

      {mode === "idle" && task.status !== "DELIVERED" && (
        <>
          {task.assignedTo && !isMine && !isManager && (
            <SectionCard>
              <Text fz="md" c="gray.6">
                {task.assignedTo.name} is carrying this one. Only they or the
                Store Manager can close it.
              </Text>
            </SectionCard>
          )}

          {!task.assignedTo && canTake && (
            <StickyActionBar
              primary={
                <Button
                  size="md"
                  fullWidth
                  loading={busy}
                  onClick={() =>
                    void run(async () => {
                      await api.assignDelivery(task.id);
                    })
                  }
                >
                  Take this delivery
                </Button>
              }
            />
          )}

          {task.status === "FAILED" && canTake && task.assignedTo && (
            <StickyActionBar
              primary={
                <Button
                  size="md"
                  fullWidth
                  loading={busy}
                  onClick={() =>
                    void run(async () => {
                      await api.assignDelivery(task.id);
                    })
                  }
                >
                  Try again
                </Button>
              }
            />
          )}

          {canClose && task.status === "ASSIGNED" && (
            <StickyActionBar
              secondary={
                <Button variant="subtle" color="gray" onClick={() => setMode("fail")}>
                  Could not deliver
                </Button>
              }
              context={
                task.collectsMoney
                  ? `Collect ${formatINR(task.outstandingAmount)}`
                  : undefined
              }
              primary={
                <Button size="md" fullWidth onClick={() => setMode("deliver")}>
                  Hand it over
                </Button>
              }
            />
          )}
        </>
      )}

      {mode === "deliver" && (
        <CaptureSheet
          kind="DELIVERY"
          title="Handover"
          description="A photo, your location and the customer's signature. All three are required."
          submitLabel="Record the handover"
          disabled={!collectedValid}
          onCancel={() => setMode("idle")}
          onSubmit={async (capture) => {
            await api.completeDelivery(task.id, {
              capture: {
                ...(capture.gps ? { gps: capture.gps } : {}),
                ...(capture.photoDataUrl ? { photoDataUrl: capture.photoDataUrl } : {}),
                ...(capture.signatureDataUrl
                  ? { signatureDataUrl: capture.signatureDataUrl }
                  : {}),
                ...(capture.notes ? { notes: capture.notes } : {}),
                clientCapturedAt: capture.clientCapturedAt,
              },
              ...(task.collectsMoney && String(collected).trim() !== ""
                ? { amountCollected: collectedAmount }
                : {}),
              ...(receivedBy.trim() ? { receivedByName: receivedBy.trim() } : {}),
            });
            await load();
            setMode("idle");
          }}
        >
          {/* Its own column since migration 14, not a line in the free-text
              note. It is the first thing asked when a delivery is disputed,
              and a note cannot be searched or reported on.

              Optional on purpose: goods go to whoever is behind the counter,
              and a required field here would fill the column with the word
              "customer". */}
          <SectionCard title="Received by">
            <Stack gap="sm">
              <TextInput
                id="received-by"
                label="Who took it"
                placeholder="Ravi Teja (counter staff)"
                maxLength={200}
                value={receivedBy}
                onChange={(event) => setReceivedBy(event.currentTarget.value)}
                description="A name and, if it helps, what they do. Left blank if nobody gave one."
              />
            </Stack>
          </SectionCard>

          {task.collectsMoney && (
            <SectionCard title="Money taken at the door">
              <Stack gap="sm">
                <MoneyField
                  id="collected"
                  label="Collected at the door"
                  min={0}
                  max={Number(task.outstandingAmount)}
                  value={collected}
                  onChange={setCollected}
                  error={
                    !collectedValid
                      ? `Enter an amount between ${formatINR(0)} and ${formatINR(
                          task.outstandingAmount,
                        )} — that is all that is outstanding.`
                      : undefined
                  }
                />
                {/* Said plainly because the two are separate records and an
                    agent should not think this one replaces the other. */}
                <Text fz="sm" c="gray.6">
                  This is noted against the delivery so the two can be
                  reconciled. The receipt itself is recorded against the bill,
                  with its own proof.
                </Text>
              </Stack>
            </SectionCard>
          )}
        </CaptureSheet>
      )}

      {mode === "fail" && (
        <SectionCard title="Could not deliver">
          <Stack gap="sm">
            <Text fz="md" c="gray.7">
              Say what stopped it. No signature is asked for — there was nobody
              to give one. The delivery stays open for another attempt.
            </Text>
            <Textarea
              id="failure-reason"
              maxLength={500}
              rows={3}
              placeholder="Shutter down, customer not answering the phone"
              value={failureReason}
              onChange={(event) => setFailureReason(event.currentTarget.value)}
            />
            <Button
              size="md"
              disabled={busy || failureReason.trim().length < 3}
              onClick={confirmFailure}
            >
              Record the failed attempt
            </Button>
            <Button
              variant="subtle"
              color="gray"
              disabled={busy}
              onClick={() => setMode("idle")}
            >
              Cancel
            </Button>
          </Stack>
        </SectionCard>
      )}

      {task.proofs.length > 0 && (
        <SectionCard title="Proof">
          <Stack gap={4}>
            {task.proofs.map((proof) => (
              <Text key={proof.id} fz="sm" c="gray.7">
                {new Date(proof.capturedAt).toLocaleString()}
                {proof.capturedBy ? ` · ${proof.capturedBy.name}` : ""}
                {proof.notes ? ` · ${proof.notes}` : ""}
              </Text>
            ))}
          </Stack>
        </SectionCard>
      )}
    </Page>
  );
}
